import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Overline } from "../../components";
import {
  CollecticonChevronDownSmall,
  CollecticonChevronLeftSmall,
  CollecticonChevronRightSmall,
  CollecticonEye,
  CollecticonEyeDisabled,
  CollecticonKeyboard,
} from "@devseed-ui/collecticons-chakra";
import ImageControls from "../../components/ImageControls";
import InferenceFilters from "../../components/InferenceFilters";
import KeyboardShortcutsModal from "./KeyboardShortcutsModal";

type ValidateHeaderProps = {
  borderColor: string;
  sceneId: string;
  sceneDate: string;
  currentIndex: number;
  inferencesSortedLength: number;
  nextInferenceId: number | false;
  previousInferenceId: number | false;
  isValidating: boolean;
  brightness: number;
  setBrightness: (value: number) => void;
  contrast: number;
  setContrast: (value: number) => void;
  bboxMask: boolean;
  setBboxMask: (value: boolean) => void;
  compositeMode: boolean;
  setCompositeMode: (value: boolean) => void;
  shortcutsModalIsOpen: boolean;
  onShortcutsModalOpen: () => void;
  onShortcutsModalClose: () => void;
};
function ValidateHeader({
  borderColor,
  sceneId,
  sceneDate,
  currentIndex,
  inferencesSortedLength,
  nextInferenceId,
  previousInferenceId,
  isValidating,
  brightness,
  setBrightness,
  contrast,
  setContrast,
  bboxMask,
  setBboxMask,
  compositeMode,
  setCompositeMode,
  shortcutsModalIsOpen,
  onShortcutsModalOpen,
  onShortcutsModalClose,
}: ValidateHeaderProps) {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const {
    isOpen: isFilterOpen,
    onToggle: onFiltersToggle,
    onClose: onFiltersClose,
  } = useDisclosure();
  return (
    <Box
      display="flex"
      px="4"
      py="3"
      gap="4"
      alignItems="center"
      borderBottom="1px"
      borderColor={borderColor}
    >
      <Box>
        <Button
          size="sm"
          variant="base-soft-outline"
          as={Link}
          to={`/scenes/${sceneId}`}
        >
          Exit
        </Button>
      </Box>
      <Box mx="auto" display="flex" alignItems="baseline" fontSize="sm" gap={2}>
        <Overline>Scene</Overline>
        <Text fontWeight="semibold">{sceneDate}</Text>/{" "}
        <Overline>Detection</Overline>{" "}
        <Text fontWeight="semibold">
          {currentIndex + 1} of {inferencesSortedLength}
        </Text>
      </Box>
      <Button
        onClick={() => setCompositeMode(!compositeMode)}
        colorScheme="purple"
        variant={compositeMode ? "solid" : "base-soft-outline"}
        size="sm"
        leftIcon={
          compositeMode ? <CollecticonEye /> : <CollecticonEyeDisabled />
        }
        title={
          compositeMode
            ? "Composite mode: Detection counts show grouped inferences"
            : "Composite mode off"
        }
      >
        Composite Mode
      </Button>
      <Popover
        isOpen={isFilterOpen}
        onClose={onFiltersClose}
        placement="bottom-end"
      >
        <PopoverTrigger>
          <Button
            rightIcon={<CollecticonChevronDownSmall />}
            onClick={onFiltersToggle}
            variant="primary-soft-outline"
            size="sm"
          >
            Filters
          </Button>
        </PopoverTrigger>
        <PopoverContent minW="fit-content" p={4}>
          <InferenceFilters />
        </PopoverContent>
      </Popover>
      <Popover isOpen={isOpen} onClose={onClose} placement="bottom-end">
        <PopoverTrigger>
          <Button
            rightIcon={<CollecticonChevronDownSmall />}
            onClick={onToggle}
            variant="primary-soft-outline"
            size="sm"
          >
            Image Controls
          </Button>
        </PopoverTrigger>
        <PopoverContent w="16rem" p={4} pb={6}>
          <ImageControls
            brightness={brightness}
            setBrightness={setBrightness}
            contrast={contrast}
            setContrast={setContrast}
          />
          <Checkbox
            name="bbox-mask-checkbox"
            size="sm"
            isChecked={bboxMask}
            mt={4}
            fontWeight="semibold"
            colorScheme="primary"
            onChange={(e) => setBboxMask(e.target.checked)}
          >
            Show Mask
          </Checkbox>
        </PopoverContent>
      </Popover>
      <IconButton
        aria-label="Keyboard shortcuts"
        title="Keyboard shortcuts"
        onClick={onShortcutsModalOpen}
        variant="base-soft-outline"
        size="sm"
        sx={{ touchAction: "manipulation" }}
      >
        <CollecticonKeyboard />
      </IconButton>
      <KeyboardShortcutsModal
        isOpen={shortcutsModalIsOpen}
        onClose={onShortcutsModalClose}
      />
      <Box display="flex" gap="2">
        <Button
          size="sm"
          variant="base-text"
          title="Navigate to the previous detection"
          isDisabled={currentIndex === 0 || isValidating}
          leftIcon={<CollecticonChevronLeftSmall />}
          as={Link}
          to={
            currentIndex === 0 || isValidating
              ? ""
              : `/scenes/${sceneId}/validate/${previousInferenceId}`
          }
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="base-text"
          title="Navigate to the next detection"
          isDisabled={
            currentIndex === inferencesSortedLength - 1 || isValidating
          }
          rightIcon={<CollecticonChevronRightSmall />}
          as={Link}
          to={
            currentIndex === inferencesSortedLength - 1 || isValidating
              ? ""
              : `/scenes/${sceneId}/validate/${nextInferenceId}`
          }
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

export default ValidateHeader;
